import React from 'react';
import { Progress } from '@/components/ui/progress';
import { Spinner } from '@/components/ui/Spinner';

type BannerVariant = 'neutral' | 'warning' | 'destructive';

interface FlowToastBannerProps {
  title: string;
  description?: string;
  progress?: number; // 0-100
  showSpinner?: boolean;
  variant?: BannerVariant;
  spinnerPosition?: 'left' | 'right';
  rounded?: 'md' | 'full';
  className?: string;
}

export const FlowToastBanner: React.FC<FlowToastBannerProps> = ({
  title,
  description,
  progress,
  showSpinner,
  variant = 'neutral',
  spinnerPosition = 'right',
  rounded = 'md',
  className
}) => {
  const pct = Math.max(0, Math.min(100, Math.round(progress ?? 0)));

  const containerBase = 'border p-3 text-sm w-[320px] shadow-md';
  const roundedClass = rounded === 'full' ? 'rounded-full' : 'rounded-md';
  const toneClass = (() => {
    switch (variant) {
      case 'warning':
        return 'bg-orange-50 border-orange-200';
      case 'destructive':
        return 'bg-destructive/10 border-destructive/30';
      default:
        return 'bg-background';
    }
  })();
  const titleClass = (() => {
    switch (variant) {
      case 'warning':
        return 'font-medium mb-1 truncate text-orange-700';
      case 'destructive':
        return 'font-medium mb-1 truncate text-destructive';
      default:
        return 'font-medium mb-1 truncate';
    }
  })();
  const descClass = variant === 'neutral' ? 'text-xs text-muted-foreground mb-2 truncate' : 'text-xs mb-2 truncate text-current/80';

  return (
    <div className={[containerBase, roundedClass, toneClass, className].filter(Boolean).join(' ')}>
      <div className="flex items-center gap-3">
        {showSpinner && spinnerPosition === 'left' ? (
          <Spinner size="sm" variant="muted" ariaLabel="Working" />
        ) : null}
        <div className="min-w-0">
          <div className={titleClass}>{title}</div>
          {description ? (
            <div className={descClass}>{description}</div>
          ) : null}
        </div>
        {showSpinner && spinnerPosition === 'right' ? (
          <Spinner size="sm" variant="muted" ariaLabel="Working" />
        ) : null}
      </div>
      {typeof progress === 'number' ? (
        <Progress value={pct} className="h-2" indicatorClassName="transition-transform duration-500 ease-out" />
      ) : null}
    </div>
  );
};

export default FlowToastBanner;
